import { GROUPING_BASIS } from 'utils/groupLabel';

// What the Athlete Import page tells an admin about the file to upload: the
// description above the file picker, the words in it that are shown in bold, and
// the template to download.
//
// A club that groups its athletes by year level has one more required column,
// [Year Level], and a template that has it. The server refuses such a club's
// file without that column (ImportResolutionService), and writes the same
// column in the file from Export to File on the Athletes page. An age
// club's file has no such column, and its text here is as it has always been.
//
// A club that has teams (a school's houses or factions) has one more optional
// column, [Team], and a template that has it. The column is headed Team
// whatever the club calls its teams, because the server matches that name;
// what goes in it is described in the club's own word. A club without teams
// is told nothing about it.
//
// Pure text, kept apart from config/admin/importTypes.js so that it can be
// tested: memberImportText.test.js.

const BEFORE_COLUMNS =
  'Select a CSV file of athletes to import. A file from [Export to File] on the Athletes page is already in this format. Columns are matched by their header name, so they can be in any order, and any extra columns are ignored. ';

const OPTIONAL_COLUMNS =
  'Optional columns: [Primary Contact First Name] and [Primary Contact Last Name]. ';

const AFTER_COLUMNS =
  '[Date of Birth] must use a four-digit year (for example 2015-01-07) — the importer auto-detects the format ([YYYY-MM-DD] or [DD/MM/YYYY]) and shows it in the preview. [Gender] accepts M, F, Male, or Female. [Bib Number] must be greater than 0. A [Member Status] of ACTIVE, CURRENT, or YES is treated as active; anything else is inactive.';

const OPTIONAL_COLUMNS_WITH_TEAM =
  'Optional columns: [Primary Contact First Name], [Primary Contact Last Name], and [Team]. ';

const AGE_REQUIRED_COLUMNS =
  'Required columns: [First Name], [Last Name], [Date of Birth], [Gender], [Bib Number], [Member Status], and [Primary Contact Email]. ';

const YEAR_LEVEL_REQUIRED_COLUMNS =
  'Required columns: [First Name], [Last Name], [Date of Birth], [Gender], [Year Level], [Bib Number], [Member Status], and [Primary Contact Email]. ';

const YEAR_LEVEL_COLUMN =
  "[Year Level] is the athlete's school year, from 0 to 12, written as a number or with a Y (5 or Y5); use 0 for the year before Year 1. ";

// What goes in the Team column, in the club's words (utils/teamLabel.js).
const teamColumn = (words) =>
  `[Team] is the name of one of your ${words.lower.teams}, as it is on the ${words.teams} page; leave it blank to keep the ${words.lower.team} the athlete has now. `;

const AGE_TEXT = {
  fileDescription:
    BEFORE_COLUMNS + AGE_REQUIRED_COLUMNS + OPTIONAL_COLUMNS + AFTER_COLUMNS,
  boldPatterns: [
    '[Sport80]',
    '[Export to File]',
    '[Primary Contact First Name]',
    '[Primary Contact Last Name]',
    '[Primary Contact Email]',
    '[First Name]',
    '[Last Name]',
    '[Date of Birth]',
    '[Gender]',
    '[Bib Number]',
    '[Member Status]',
    '[YYYY-MM-DD]',
    '[DD/MM/YYYY]',
  ],
  templates: [
    {
      name: 'CSV file template',
      url: '/templates/member-import-template-splitfast.csv',
    },
  ],
};

const YEAR_LEVEL_TEXT = {
  fileDescription:
    BEFORE_COLUMNS +
    YEAR_LEVEL_REQUIRED_COLUMNS +
    OPTIONAL_COLUMNS +
    YEAR_LEVEL_COLUMN +
    AFTER_COLUMNS,
  boldPatterns: [...AGE_TEXT.boldPatterns, '[Year Level]'],
  templates: [
    {
      name: 'CSV file template',
      url: '/templates/member-import-template-year-level.csv',
    },
  ],
};

// The same two, for a club that has teams: the Team column is described and
// the template has it.
const withTeam = (text, requiredColumns, groupColumn, templateUrl, words) => ({
  fileDescription:
    BEFORE_COLUMNS +
    requiredColumns +
    OPTIONAL_COLUMNS_WITH_TEAM +
    groupColumn +
    teamColumn(words) +
    AFTER_COLUMNS,
  boldPatterns: [...text.boldPatterns, '[Team]'],
  templates: [{ name: 'CSV file template', url: templateUrl }],
});

// groupingBasis: how the club groups its athletes (utils/groupLabel.js).
// teamWords: the club's words for a team (teamWordsOf in utils/teamLabel.js)
// when the club has teams, and nothing when it has none.
export const memberImportText = (groupingBasis, teamWords) => {
  const byYearLevel = groupingBasis === GROUPING_BASIS.YEAR_LEVEL;
  if (!teamWords) return byYearLevel ? YEAR_LEVEL_TEXT : AGE_TEXT;
  return byYearLevel
    ? withTeam(
        YEAR_LEVEL_TEXT,
        YEAR_LEVEL_REQUIRED_COLUMNS,
        YEAR_LEVEL_COLUMN,
        '/templates/member-import-template-year-level-teams.csv',
        teamWords
      )
    : withTeam(
        AGE_TEXT,
        AGE_REQUIRED_COLUMNS,
        '',
        '/templates/member-import-template-teams.csv',
        teamWords
      );
};
